import {
  defer,
  type LinksFunction,
  type LoaderArgs,
  type AppLoadContext } from
'@shopify/remix-oxygen';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
  useLocation } from
'@remix-run/react';
import { ShopifySalesChannel, Seo } from '@shopify/hydrogen';
import { Layout } from '~/components';
import { GenericError } from './components/GenericError';
import { NotFound } from './components/NotFound';
import GoTop from './components/_App/GoTop';
import { Toaster } from 'react-hot-toast';
import styles from './styles/bootstrap.min.css';
import animate from 'animate.css';
import boxicons from './styles/boxicons.min.css';
import flaticons from './styles/flaticon.css';
import slick from './styles/slick.css';
import accordion from 'react-accessible-accordion/dist/fancy-example.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Swiper from 'swiper/swiper-bundle.css';
// Global Style
import globalStyles from './styles/style.css';
import customStyles from './styles/custom.css';
// Global Responsive Style
import responsive from './styles/responsive.css';
import { seoPayload } from '~/lib/seo.server';
import {
  DEFAULT_LOCALE,
  parseMenu,
  getCartId,
  type EnhancedMenu } from
'./lib/utils';
import invariant from 'tiny-invariant';
import { Shop, Cart } from '@shopify/hydrogen/storefront-api-types';
import { useAnalytics } from './hooks/useAnalytics';
import {
  findValueFromFields,
  get,
  getBrandingDetails,
  getDefaultCache,
  getPageSectionBlocks,
  getSiteDetails,
  getValueFromReference,
  logger } from
'./utils';
import { IRoute, MetafieldStringData } from './types';
import { CACHE_SHORT } from './data/cache';
import _ from 'lodash';
import { fetchSite } from './lib/storefrontFetcher';
import NavbarSection from './components/Sections/NavbarSection';
import { getConfig } from './lib/config';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
export const links: LinksFunction = () => {
  return [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: responsive },
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'stylesheet', href: slick },
  { rel: 'stylesheet', href: accordion },
  { rel: 'stylesheet', href: boxicons },
  { rel: 'stylesheet', href: flaticons },
  { rel: 'stylesheet', href: animate },
  { rel: 'stylesheet', href: Swiper },
  { rel: 'stylesheet', href: customStyles },
  {
    rel: 'preconnect',
    href: 'https://cdn.shopify.com'
  },
  {
    rel: 'preconnect',
    href: 'https://shop.app'
  }];

};

export async function loader({ request, context }: LoaderArgs) {
  const cartId = getCartId(request);

  const [customerAccessToken, layout] = await Promise.all([
  context.session.get('customerAccessToken'),
  getLayoutData(context)]
  );
  const isAuthenticated = Boolean(customerAccessToken);
  const seo = seoPayload.root({ shop: layout.shop, url: request.url });
  const env = context.env;
  const { meta, header, pages, menu, footer } = await getSiteDetails(
    context,
    layout.site
  );
  const { GOOGLE_CLIENT_ID } = getConfig({ context });
  return defer(
    {
      header,
      meta,
      pages,
      footer,
      isLoggedIn: Boolean(customerAccessToken),
      layout,
      isAuthenticated,
      selectedLocale: context.storefront.i18n,
      cart: cartId ? getCart(context, cartId) : undefined,
      analytics: {
        shopifySalesChannel: ShopifySalesChannel.hydrogen,
        shopId: layout.shop.id
      },
      seo,
      GOOGLE_CLIENT_ID,
      menu,
      env
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT
      }
    }
  );
}

export default function App() {
  const location = useLocation();
  const data = useLoaderData<typeof loader>();
  const env = get(data, 'env');
  ReactGA.initialize(env?.GOOGLE_TAG || "G-HBWELS4EJ2");


  // let botUrl = 'https://webapp-chat-box-simplechat-pl87.vercel.app/';
  let botUrl = 'https://bot.omnivoltaic.com/';
  // let botUrl = 'https://chatbot.omnivoltaic.com/'
  try {
    if (env?.NODE_ENV === 'development') {
      // botUrl = 'https://webapp-chat-box-simplechat-pl87.vercel.app/';
      botUrl = 'https://bot.omnivoltaic.com/';
    }
  } catch (error) {
    console.error(error);
  }
  const [iframeLink, setIframeLink] = useState<string>(botUrl);
  const locale = data.selectedLocale ?? DEFAULT_LOCALE;
  const hasUserConsent = true;
  const [chatButtonClicked, setChatButtonClicked] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  useAnalytics(hasUserConsent, locale);

  useEffect(() => {
    if (window.innerWidth > 500 && chatButtonClicked) {
      let iframeid = document.getElementById('bot-iframe');
      console.log(iframeid, "Iframe Id");
      if (iframeid) {
        iframeid.style.height = `550px`;
        iframeid.style.width = `400px`;
      }
    } else if (window.innerWidth < 500 && chatButtonClicked) {
      let iframeid = document.getElementById('bot-iframe');
      if (iframeid) {
        iframeid.style.width = `${window.innerWidth}px`;
        iframeid.style.height = `${window.innerHeight}px`;
      }
    }
    window.addEventListener('resize', () => {
      if (window.innerWidth < 500 && chatButtonClicked) {
        let iframeid = document.getElementById('bot-iframe');
        if (iframeid) {
          iframeid.style.width = `${window.innerWidth}px`;
          iframeid.style.height = `${window.innerHeight}px`;
        }
      } else if (window.innerWidth > 500 && chatButtonClicked) {
        let iframeid = document.getElementById('bot-iframe');
        if (iframeid) {
          iframeid.style.height = `550px`;
          iframeid.style.width = `400px`;
        }
      }
      const parentWindowWidth = window.innerWidth;
      const parentWindowHeight = window.innerHeight;
      const iframe = document.querySelector('iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            width: parentWindowWidth,
            height: parentWindowHeight
          },
          '*'
        );
      }
    });
  }, [chatButtonClicked]);


  useEffect(() => {
    const handlePageLoad = () => {
      setIsPageLoaded(true);
    };

    // Check if page has already loaded
    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      // Add event listener for page load
      window.addEventListener("load", handlePageLoad);

      // Cleanup listener on component unmount
      return () => {
        window.removeEventListener("load", handlePageLoad);
      };
    }
  }, []);
  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.data.width) {
        var iframe = document.getElementById('bot-iframe');
        if (iframe) {
          iframe.style.width = event.data.width;
          iframe.style.height = event.data.height;
        }
        if (event.data.sender === 'Close') {
          setChatButtonClicked(false);
          if (iframe) {
            iframe.style.right = '40px';
            iframe.style.bottom = '13px';
          }
        } else if (event.data.sender === 'Open') {
          setChatButtonClicked(true);
          if (iframe) {
            iframe.style.right = '5px';
            iframe.style.bottom = '5px';
          }
        }
      }
    };

    window.addEventListener('message', handleMessage, false);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);
  useEffect(() => {
    setTimeout(() => {
      const iframe = document.querySelector('iframe');
      const parentWindowWidth = window.innerWidth;
      const parentWindowHeight = window.innerHeight;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            width: parentWindowWidth,
            height: parentWindowHeight
          },
          '*'
        );
      }
    }, 500);
  }, []);

  useEffect(() => {
    const newIframeSrc = `${botUrl}?page=${encodeURIComponent(
      window.location.href
    )}`;
    setIframeLink(newIframeSrc);
  }, [location.pathname]); // Dependency on location.pathname

  return (
    <html lang={locale.language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Seo />
        <Meta />
        <link
          href="https://fonts.googleapis.com/css?family=Poppins:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i"
          rel="stylesheet">
        </link>
        <link
          rel="icon"
          type="image/x-icon"
          href={get(data, 'meta.favicon')}>
        </link>
        <Links />
      </head>
      <body>
        <Layout footer={get(data, 'footer')} meta={get(data, 'meta')}>
          <GoogleOAuthProvider clientId={data?.GOOGLE_CLIENT_ID}>
            <Outlet
              context={{
                isAuthenticated: data.isAuthenticated,
                menuItems: get(data, 'menuItems'),
                menu: get(data, 'menu'),
                logo: get(data, 'header.logo')
              }} />

          </GoogleOAuthProvider>
          <Toaster position="top-right" />

          <GoTop scrollStepInPx="100" delayInMs="10.50" />
          {
          isPageLoaded &&
          <iframe
            title="Chatbot"
            src={iframeLink}
            width="80"
            height="80"
            className="iframe"
            id="bot-iframe"
            style={{
              position: 'fixed',
              bottom: '13px',
              right: '40px',
              zIndex: '9999',
              border: 'none',
              background: 'rgba(255, 255, 255, 0)'
            }} />}


        </Layout>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>);

}

export function ErrorBoundary({ error }: {error: Error;}) {
  const [root] = useMatches();
  const locale = root?.data?.selectedLocale ?? DEFAULT_LOCALE;
  const routeError = useRouteError();
  const isRouteError = isRouteErrorResponse(routeError);
  let title = 'Error';
  let pageType = 'page';
  if (isRouteError) {
    title = 'Not found';
    if (routeError.status === 404) pageType = routeError.data || pageType;
  }

  return (
    <html lang={locale.language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>{title}</title>
        <Meta />
        <Links />
      </head>
      <body>
        <Layout
          layout={root?.data?.layout}
          key={`${locale.language}-${locale.country}`}>

          <NavbarSection
            menuItems={(root?.data?.menuItems as IRoute[])}
            logo={root?.data?.logo}
            title={findValueFromFields(root?.data?.home_page, 'title')} />

          {isRouteError ?
          <>
              {routeError.status === 404 ?
            <NotFound type={pageType} /> :

            <GenericError
              error={{ message: `${routeError.status} ${routeError.data}` }} />}


            </> :

          <GenericError error={error instanceof Error ? error : undefined} />}

        </Layout>
        <Scripts />
      </body>
    </html>);

}

const LAYOUT_QUERY = `#graphql
  query layoutMenus(
    $language: LanguageCode
    $headerMenuHandle: String!
    $footerMenuHandle: String!
  ) @inContext(language: $language) {
    shop {
      id
      name
      description
      primaryDomain {
        url
      }
      brand {
       logo {
         image {
          url
         }
       }
     }
    }
    headerMenu: menu(handle: $headerMenuHandle) {
      id
      items {
        ...MenuItem
        items {
          ...MenuItem
        }
      }
    }
    footerMenu: menu(handle: $footerMenuHandle) {
      id
      items {
        ...MenuItem
        items {
          ...MenuItem
        }
      }
    }
  }
  fragment MenuItem on MenuItem {
    id
    resourceId
    tags
    title
    type
    url
  }
`;

export interface LayoutData {
  headerMenu: EnhancedMenu;
  footerMenu: EnhancedMenu;
  shop: Shop;
  cart?: Promise<Cart>;
  branding?: {
    fields: MetafieldStringData[];
  };
}

export interface StrofrontData {
  metaobject: {
    fields: MetafieldStringData[];
  };
}

async function getLayoutData(context: AppLoadContext) {
  const { storefront, env } = context;
  const HEADER_MENU_HANDLE = 'main-menu';
  const FOOTER_MENU_HANDLE = 'footer';

  const data = await storefront.query<LayoutData>(LAYOUT_QUERY, {
    variables: {
      headerMenuHandle: HEADER_MENU_HANDLE,
      footerMenuHandle: FOOTER_MENU_HANDLE,
      language: storefront.i18n.language
    },
    cache: getDefaultCache(storefront)
  });

  const site = await fetchSite({ context });

  invariant(data, 'No data returned from Shopify API');

  /*
    Modify specific links/routes (optional)
    @see: https://shopify.dev/api/storefront/unstable/enums/MenuItemType
    e.g here we map:
      - /blogs/news -> /news
      - /blog/news/blog-post -> /news/blog-post
      - /collections/all -> /products
  */
  const customPrefixes = { BLOG: '', CATALOG: 'products' };

  const headerMenu = data?.headerMenu ?
  parseMenu(data.headerMenu, customPrefixes) :
  undefined;

  const footerMenu = data?.footerMenu ?
  parseMenu(data.footerMenu, customPrefixes) :
  undefined;

  return { shop: data.shop, headerMenu, footerMenu, site };
}

const CART_QUERY = `#graphql
  query CartQuery($cartId: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }

  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    buyerIdentity {
      countryCode
      customer {
        id
        email
        firstName
        lastName
        displayName
      }
      email
      phone
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            amountPerQuantity {
              amount
              currencyCode
            }
            compareAtAmountPerQuantity {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              availableForSale
              compareAtPrice {
                ...MoneyFragment
              }
              price {
                ...MoneyFragment
              }
              requiresShipping
              title
              image {
                ...ImageFragment
              }
              product {
                handle
                title
                id
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        ...MoneyFragment
      }
      totalAmount {
        ...MoneyFragment
      }
      totalDutyAmount {
        ...MoneyFragment
      }
      totalTaxAmount {
        ...MoneyFragment
      }
    }
    note
    attributes {
      key
      value
    }
    discountCodes {
      code
    }
  }

  fragment MoneyFragment on MoneyV2 {
    currencyCode
    amount
  }

  fragment ImageFragment on Image {
    id
    url
    altText
    width
    height
  }
`;

export async function getCart({ storefront }: AppLoadContext, cartId: string) {
  invariant(storefront, 'missing storefront client in cart query');

  const { cart } = await storefront.query<{cart?: Cart;}>(CART_QUERY, {
    variables: {
      cartId,
      country: storefront.i18n.country,
      language: storefront.i18n.language
    },
    cache: getDefaultCache(storefront)
  });

  return cart;
}